/**
 * __h2kRenderState — dev/test-gated render-truth introspection.
 *
 * Exposes `window.__h2kRenderState()` which returns the entities the client is
 * *currently rendering* (projectiles, nameplates, effects, chat bubbles, cast
 * bars), keyed by ownerId + position/size. A 2-client test reads this on BOTH
 * clients and asserts parity from structured data instead of brittle pixels.
 *
 * Design notes:
 * - GATED at boot on `?testhooks=1` (or `?vqa=1` so existing harness URLs work).
 *   When the flag is absent the global is never defined — zero footprint in prod.
 * - PULL MODEL: nothing runs per-frame. The snapshot is computed only when the
 *   test calls `window.__h2kRenderState()`. So even when enabled there is no tick
 *   cost.
 * - Every source read is wrapped so a not-yet-ready subsystem yields `[]` rather
 *   than throwing — the hook must never break a live client.
 *
 * Sibling of `src/game/vqa/__vqa_hook.ts`; installed alongside it in main.tsx.
 */

import { gameLogger } from '../../utils/GameLogger';
import { Services, PlayerServices, CombatServices } from '../core/Services';
import { ServiceRegistry } from '../core/ServiceRegistry';
import { resolveTargetPosition } from '../systems/projectile/ProjectileAimResolver';
import { VisualLifecycleStore } from '../diagnostics/VisualLifecycleStore';
import { getAbilitiesForClass } from '../../shared/content/AbilityDataAdapter';
import { DebugFCT } from '../debug/DebugFCT';
import { drawnProjectilePositions } from '../rendering/ProjectileRenderer';
import { TrailSystem } from '../effects/players/TrailSystem';
import { EventBus, EVENTS } from '../../shared/EventBus';

// Canonical wire shapes live in shared (the capture sidecar + server type
// against them); re-exported here so existing consumers keep their import.
import type { H2kProjectile, H2kEffect, H2kCastBar } from '../../shared/diagnostics/CaptureSidecarTypes';
import type { RemoteInteractableDiagnostics } from '../systems/network/RemoteInteractableManager';
import { bootPathname, bootSearchParams } from '../../config/boot/bootUrl';

export type { H2kProjectile, H2kEffect, H2kCastBar };

export interface H2kNameplate {
  ownerId: string | null;
  name: string | null;
  class: string | null;
  health: number | null;
  maxHealth: number | null;
  mana: number | null;
  maxMana: number | null;
  x: number | null;
  y: number | null;
  isLocal: boolean;
  spriteExists: boolean;
}

export interface H2kChatBubble {
  ownerId: string | null;
  text: string;
  x: number | null;
  y: number | null;
  alive: boolean;
}

/**
 * The interactables row IS whatever `RemoteInteractableManager.getDiagnostics()`
 * returns — `captureInteractables()` below only spreads it. This was a
 * hand-written structural copy of that return type, with the phase/renderer
 * unions re-spelled inline instead of following `LootVisualPhase` and the
 * visual's own diagnostics shape. Aliased so a rename upstream is a compile
 * error here, the same way the projectile/effect/cast-bar shapes above defer
 * to the shared capture-sidecar types. `import type` erases, so no runtime edge.
 */
export type H2kInteractable = RemoteInteractableDiagnostics;

export interface H2kProjectileDespawn {
  id: string;
  ownerId: string | null;
  abilityId: string | null;
  reason: string;
  ageMs: number;
  x: number;
  y: number;
}

export interface H2kRenderState {
  ts: number;
  localSessionId: string | null;
  projectiles: H2kProjectile[];
  /** Last ~20 projectile despawns with attribution (Lane E forensics) —
   *  lets a 2-client run answer WHY the observer's copy died early. */
  projectileDespawns: H2kProjectileDespawn[];
  /** Live projectile ribbons: how long a trail outlives its projectile. */
  trails?: ReturnType<typeof TrailSystem.getRibbonRows>;
  effects: H2kEffect[];
  nameplates: H2kNameplate[];
  /** Server-authored world loot plus its current Pixi motion phase. */
  interactables?: H2kInteractable[];
  chatBubbles: H2kChatBubble[];
  castBars: H2kCastBar[];
}

/**
 * Optional aim for `__h2kCast`. When omitted, the cast fires with the
 * caster's ambient targeting/facing state (back-compat — the no-arg form
 * still works for existing specs/harness).
 *
 * - `targetId`: select this entity via TargetingSystem before casting, so
 *   the local projectile's ProjectileAimResolver homes/aims at it — exactly
 *   the same input the OBSERVER's remote path receives via the broadcast
 *   `target_id`. This is what closes the "caster's local projectile has 0
 *   velocity while the observer's flies" parity gap.
 * - `targetX`/`targetY`: when no targetId is available, point the caster's
 *   `facing` toward this world point so the facing-fallback aim flies the
 *   right direction deterministically (instead of inheriting whatever
 *   stale facing the player happened to have).
 */
export interface H2kCastAim {
  targetId?: string;
  targetX?: number;
  targetY?: number;
}

declare global {
  interface Window {
    __h2kRenderState?: () => H2kRenderState;
    /**
     * Drive a real CLIENT-SIDE cast (PlayerManager.performAbility), which both
     * creates the local projectile/VFX AND emits the broadcast intent — the
     * actual gameplay path. `__vqa.useAbility` only sends castAbility to the
     * server (no local execution), so it can't be used for local-render parity.
     *
     * `aim` is OPTIONAL — `__h2kCast('fireball')` (no aim) still works. Pass
     * `{ targetId }` (or `{ targetX, targetY }`) to give the local cast a real
     * aim so the caster's own projectile resolves a non-zero velocity that
     * matches what the observer renders.
     */
    __h2kCast?: (abilityId: string, aim?: H2kCastAim) => void;
    /**
     * Visual Lifecycle event log (VisualLifecycleStore.all()) — lets the
     * ability-audit harness diff "which visual events did THIS cast produce"
     * (cast VFX / projectile / impact / hit VFX / FCT / status) without
     * scraping the monitor window. Read-only snapshot.
     */
    __h2kVisualLifecycle?: () => readonly unknown[];
    /**
     * Ability census for a class (abilities.gen getAbilitiesForClass) — the
     * audit harness enumerates the real data-driven ability list instead of
     * hardcoding ids.
     */
    __h2kAbilities?: (classId: string) => Record<string, unknown>;
    /**
     * DebugFCT history (cast-stage narrative incl. gate rejections) — the
     * audit reads this to attribute "no visuals" to a real reject reason
     * (gate-los / gate-range / gate-mana fail) instead of calling it dead.
     */
    __h2kCastLog?: () => readonly { time: number; channel: string; level: string; text: string }[];
    /**
     * Lab-only CLIENT-side cast-gate overrides (same flags the
     * AbilityTestRig OverridePanel sets on the player object) — lets the
     * ability-audit harness sweep a full 100+-ability rotation without the
     * L1 mana pool / per-ability cooldowns starving later casts into
     * gate-mana rejects. Client prediction only: the server keeps enforcing
     * its own gates (the GM /mana chat command is the server-side pair).
     */
    __h2kLabOverrides?: (flags: { infiniteMana?: boolean; noCooldowns?: boolean; noRangeGate?: boolean }) => boolean;
    /** Set or read the live TargetingSystem target. */
    __h2kSwitchTarget?: (targetId?: string) => { ok: boolean; targetId: string | null };
    /** Drive the real CastTimelineSystem cancellation path. */
    __h2kCancelCast?: (reason?: 'cancel' | 'interrupt' | 'movement') => {
      ok: boolean;
      casterId: string | null;
      reason: string;
    };
    /** Pull-only beam-link runtime truth. */
    __h2kBeam?: () => { activeCount: number; storeLen: number };
    /** Trigger the synthetic in-place switch seam used by the leak guard. */
    __h2kMapSwitchInPlace?: (toMap: string, synthetic?: boolean) => { ok: boolean; method: string };
    /** Stable readiness predicate for ordered live-probe scenarios. */
    __h2kProbeReady?: () => boolean;
    /** Verify the Grounds route and request the existing debug level grant. */
    __h2kBootGrounds?: (grantLevel?: number) => {
      ok: boolean;
      method: string;
      granted: boolean;
      level: number | null;
    };
    /** Apply a status through the same authoritative room message as on-hit effects. */
    __h2kApplyStatus?: (targetId: string, effectId: string, durationMs?: number) => {
      ok: boolean;
      method: string;
      targetId: string;
      effectId: string;
    };
  }
}

function isTestHooksEnabled(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const sp = bootSearchParams();
    return sp.get('testhooks') === '1' || sp.get('vqa') === '1';
  } catch {
    return false;
  }
}

/** Each section is independently guarded — one failing source yields []. */
function safe<T>(fn: () => T[]): T[] {
  try {
    return fn() ?? [];
  } catch {
    return [];
  }
}

function localSessionId(): string | null {
  try {
    return (window as any).nm?.getRoom?.()?.sessionId ?? null;
  } catch {
    return null;
  }
}

function captureProjectiles(): H2kProjectile[] {
  const sys = CombatServices.projectiles();
  if (!sys) return [];
  const drawn = drawnProjectilePositions();
  return sys.getProjectiles().map((p: any) => ({
    id: p.id,
    ownerId: p.ownerId ?? null,
    x: p.position?.x ?? 0,
    y: p.position?.y ?? 0,
    width: p.width,
    height: p.height,
    abilityId: p.abilityId ?? null,
    shape: p.shape ?? null,
    active: !!p.active,
    drawX: drawn.get(p.id)?.x, drawY: drawn.get(p.id)?.y,
  }));
}

function captureProjectileDespawns(): H2kProjectileDespawn[] {
  const sys = CombatServices.projectiles() as any;
  if (!sys || typeof sys.getRecentDespawns !== 'function') return [];
  return sys.getRecentDespawns().map((d: any) => ({ ...d }));
}

function captureNameplates(): H2kNameplate[] {
  const out: H2kNameplate[] = [];
  const entity = Services.entity();

  // Remote players — RemotePlayerManager.getDiagnostics() already exposes the
  // schema-backed health/mana/name/class + the rendered sprite position.
  const remotes = entity?.getRemotePlayerManager?.()?.getDiagnostics?.() ?? [];
  for (const r of remotes as any[]) {
    out.push({
      ownerId: r.sessionId ?? r.entityId ?? null,
      name: r.name ?? null,
      class: r.className ?? null,
      health: r.health ?? null,
      maxHealth: r.maxHealth ?? null,
      mana: r.mana ?? null,
      maxMana: r.maxMana ?? null,
      x: r.renderX ?? r.schemaX ?? null,
      y: r.renderY ?? r.schemaY ?? null,
      isLocal: false,
      spriteExists: !!r.spriteExists,
    });
  }

  // Local player.
  const me = PlayerServices.entity() as any;
  if (me) {
    out.push({
      ownerId: localSessionId(),
      name: me.name ?? null,
      class: me.class ?? null,
      health: typeof me.health === 'number' ? me.health : null,
      maxHealth: typeof me.maxHealth === 'number' ? me.maxHealth : null,
      mana: typeof me.mana === 'number' ? me.mana : null,
      maxMana: typeof me.maxMana === 'number' ? me.maxMana : null,
      x: me.position?.x ?? null,
      y: me.position?.y ?? null,
      isLocal: true,
      spriteExists: true,
    });
  }

  return out;
}

function captureInteractables(): H2kInteractable[] {
  const entity = Services.entity();
  const diagnostics = entity?.getRemoteInteractableManager?.()?.getDiagnostics?.() ?? [];
  return diagnostics.map((entry: H2kInteractable) => ({ ...entry }));
}

// effects / chatBubbles / castBars land in the fan-out pass (require the four
// small system getters — VFXSystem.getRenderedEffects, SceneEventManager.
// getActiveChatBubbles, CastTimelineSystem.getActiveCasts). Until then they
// report [] so the schema stays stable for the harness.
function captureEffects(): H2kEffect[] {
  const vfx = Services.vfx() as any;
  if (!vfx || typeof vfx.getRenderedEffects !== 'function') return [];
  return vfx.getRenderedEffects();
}

function captureChatBubbles(): H2kChatBubble[] {
  const sem = (window as any).__SCENE_EVENT_MANAGER__;
  if (!sem || typeof sem.getActiveChatBubbles !== 'function') return [];
  return sem.getActiveChatBubbles();
}

function captureCastBars(): H2kCastBar[] {
  const timeline = CombatServices.timeline() as any;
  if (!timeline || typeof timeline.getActiveCasts !== 'function') return [];
  return timeline.getActiveCasts();
}

function snapshot(): H2kRenderState {
  return {
    ts: Date.now(),
    localSessionId: localSessionId(),
    projectiles: safe(captureProjectiles),
    projectileDespawns: safe(captureProjectileDespawns),
    trails: safe(() => TrailSystem.getRibbonRows()),
    effects: safe(captureEffects),
    nameplates: safe(captureNameplates),
    interactables: safe(captureInteractables),
    chatBubbles: safe(captureChatBubbles),
    castBars: safe(captureCastBars),
  };
}

/**
 * Direct snapshot access for the ability-capture sidecar (gated on
 * ?abilityCapture=1 — a DIFFERENT flag than the ?testhooks=1 window hook,
 * so the capture client must not depend on window.__h2kRenderState).
 * Same pull-model: zero cost unless called.
 */
export function captureRenderStateSnapshot(): H2kRenderState {
  return snapshot();
}

/**
 * Resolve an optional aim into concrete caster state before the cast runs:
 *
 *   1. `targetId` → set it as the live target (TargetingSystem.setTarget) so
 *      PlayerManager.getCurrentTargetId() returns it and ProjectileBehavior's
 *      ProjectileAimResolver computes a velocity vector toward it.
 *   2. Point the caster's `facing` toward the aim point (targetX/targetY, or
 *      the resolved target position) so even the facing-fallback aim path
 *      (no live target position) fires in the intended direction with a
 *      deterministic, non-zero velocity.
 *
 * Best-effort: any sub-step that can't resolve (systems not ready) is skipped
 * silently. The cast still fires — worst case it inherits ambient state, which
 * is the pre-existing no-arg behavior.
 */
function applyCastAim(aim: H2kCastAim): void {
  // Facing is L/R only (the game is 2D-biased), so only the x-coordinate of
  // the aim point matters for orienting the facing-fallback shot.
  let aimX = aim.targetX;

  if (aim.targetId) {
    try {
      const targeting = ServiceRegistry.getInstance()
        .get('TargetingSystem', { optional: true });
      targeting?.setTarget?.(aim.targetId);
    } catch {
      /* targeting not ready — fall back to facing-only aim */
    }
    // If no explicit aim point was supplied, derive one from the target's
    // resolved world position so facing still points the right way.
    if (aimX === undefined) {
      try {
        const pos = resolveTargetPosition(aim.targetId);
        if (pos) aimX = pos.x;
      } catch {
        /* resolver race — facing stays as-is */
      }
    }
  }

  // Orient facing toward the aim point (deterministic L/R) so the
  // facing-fallback branch of ProjectileAimResolver fires the correct way.
  if (aimX !== undefined) {
    const me = PlayerServices.entity() as { position?: { x: number }; facing?: 'left' | 'right' } | null | undefined;
    if (me?.position) {
      me.facing = aimX >= me.position.x ? 'right' : 'left';
    }
  }
}

export function installRenderStateHook(): void {
  if (!isTestHooksEnabled()) return;
  if (typeof window === 'undefined') return;

  window.__h2kRenderState = snapshot;
  window.__h2kCast = (abilityId: string, aim?: H2kCastAim) => {
    try {
      if (aim) applyCastAim(aim);
      PlayerServices.manager()?.performAbility(abilityId);
    } catch {
      /* test surface — never throw into the client */
    }
  };
  window.__h2kVisualLifecycle = () => VisualLifecycleStore.all();
  window.__h2kAbilities = (classId: string) => getAbilitiesForClass(classId);
  window.__h2kCastLog = () => DebugFCT.getHistory();
  window.__h2kLabOverrides = (flags) => {
    const player = PlayerServices.entity() as
      | { infiniteMana?: boolean; noCooldowns?: boolean; noRangeGate?: boolean }
      | null
      | undefined;
    if (!player) return false;
    if (flags.infiniteMana !== undefined) player.infiniteMana = flags.infiniteMana;
    if (flags.noCooldowns !== undefined) player.noCooldowns = flags.noCooldowns;
    if (flags.noRangeGate !== undefined) player.noRangeGate = flags.noRangeGate;
    return true;
  };
  window.__h2kSwitchTarget = (targetId?: string) => {
    try {
      const targeting = ServiceRegistry.getInstance().get('TargetingSystem', { optional: true });
      if (!targeting) return { ok: false, targetId: null };
      if (targetId) targeting.setTarget(targetId);
      return { ok: true, targetId: targeting.getTarget() };
    } catch {
      return { ok: false, targetId: null };
    }
  };
  window.__h2kCancelCast = (reason = 'movement') => {
    try {
      const casterId = String((PlayerServices.entity() as { id?: string } | null | undefined)?.id ?? '');
      const timeline = CombatServices.timeline();
      if (!casterId || !timeline) return { ok: false, casterId: casterId || null, reason };
      timeline.cancelCast(casterId, reason);
      return { ok: true, casterId, reason };
    } catch {
      return { ok: false, casterId: null, reason };
    }
  };
  window.__h2kBeam = () => {
    try {
      const vfx = Services.vfx();
      if (!vfx) return { activeCount: -1, storeLen: -1 };
      return {
        activeCount: vfx.getActiveBeamCount(),
        storeLen: vfx.getBeamSampleCount(),
      };
    } catch {
      return { activeCount: -1, storeLen: -1 };
    }
  };
  window.__h2kMapSwitchInPlace = (toMap: string, synthetic = true) => {
    if (!synthetic) return { ok: false, method: 'handoff-not-wired' };
    try {
      EventBus.getInstance().emit(EVENTS.MAP_SERVER_SWITCH_START, { mapId: toMap });
      return { ok: true, method: 'synthetic MAP_SERVER_SWITCH_START' };
    } catch {
      return { ok: false, method: 'synthetic MAP_SERVER_SWITCH_START failed' };
    }
  };
  window.__h2kProbeReady = () =>
    typeof window.__h2kCast === 'function'
    && typeof window.__h2kSwitchTarget === 'function'
    && typeof window.__h2kCancelCast === 'function'
    && typeof window.__h2kBeam === 'function'
    && typeof window.__h2kMapSwitchInPlace === 'function';
  window.__h2kBootGrounds = (grantLevel = 30) => {
    try {
      if (bootPathname() !== '/abilitygrounds') {
        return { ok: false, method: 'wrong-route', granted: false, level: null };
      }
      const room = (window as any).nm?.getRoom?.();
      const player = room?.state?.players?.get?.(room.sessionId);
      if (!room || !player) return { ok: false, method: 'room-not-ready', granted: false, level: null };
      const level = typeof player.level === 'number' ? player.level : null;
      const granted = level === null || level < grantLevel;
      if (granted) room.send('debug_setLevel', { level: grantLevel });
      return { ok: true, method: 'debug_setLevel', granted, level };
    } catch {
      return { ok: false, method: 'debug_setLevel failed', granted: false, level: null };
    }
  };
  window.__h2kApplyStatus = (targetId: string, effectId: string, durationMs = 12_000) => {
    try {
      const room = (window as any).nm?.getRoom?.();
      if (!room?.send) return { ok: false, method: 'room-not-ready', targetId, effectId };
      room.send('applyStatusEffect', { targetId, effectId, durationMs });
      return { ok: true, method: 'room.applyStatusEffect', targetId, effectId };
    } catch {
      return { ok: false, method: 'room.applyStatusEffect failed', targetId, effectId };
    }
  };
  gameLogger.info('boot', '[__h2kRenderState] installed — render truth, ability/VFX lifecycle, and ordered live-probe hooks exposed via ?testhooks=1 (or ?vqa=1)');
}
